import React, { useState } from 'react';
import { tileCropStyle, aspectSafeSrc, isUsableBox } from '../lib/cropGeometry';

/**
 * MIRROR of the client's `src/components/CropTileImage.js` — keep in step, so
 * the thumbnail you review here is the thumbnail users get.
 *
 * A square thumbnail showing just the crop box — the wine-list tile.
 *
 * Distinct from the app's `CropCoverImage` (the hero bands) on purpose. A band
 * wants a slice of the photo positioned on the label; a tile wants the label to
 * FILL it. `object-fit:
 * cover` can only do the former — it positions but never zooms — so a square
 * tile drawn that way shows the whole width of a portrait bottle with the label
 * small in the middle.
 *
 * Filling means scaling the image so the box maps onto the tile, which needs
 * the image's true aspect. That is measured from the loaded image rather than
 * read from the database: sizing from a wrong stored dimension is what made
 * these thumbnails render as a close-up of the capsule, and — because the old
 * version paired it with `object-fit: fill` — visibly stretched as well.
 *
 * Until the measurement lands, and whenever there's no usable box, it renders a
 * plain centred cover, which is what an uncropped thumbnail should look like.
 *
 * The parent tile must be `position: relative; overflow: hidden`.
 */
export default function CropTileImage({
  box, src, tile, alt = '', className, style, ...rest
}) {
  const [natural, setNatural] = useState(null);

  const onLoad = (e) => {
    const img = e.target;
    if (img.naturalWidth && img.naturalHeight) {
      setNatural({ w: img.naturalWidth, h: img.naturalHeight });
    }
    if (rest.onLoad) rest.onLoad(e);
  };

  const crop = natural && isUsableBox(box)
    ? tileCropStyle({
      box, imgW: natural.w, imgH: natural.h, tile,
    })
    : null;

  const fallback = { width: '100%', height: '100%', objectFit: 'cover' };

  return (
    <img
      {...rest}
      src={aspectSafeSrc(src)}
      alt={alt}
      className={className}
      onLoad={onLoad}
      style={{ ...(crop || fallback), ...style }}
    />
  );
}
